/**
 * Simple HTML export function that captures the complete Tldraw canvas
 * Preserves fonts, styles, and all print-ready elements
 */

import { getEffectiveFormat, isRuntimeFormat } from "../../formats/formatsStore";
import { getExportBleedPx, getExportBleedZonePx } from "../../formats/bleed";
import { getStoredOrientation } from "../../utils/orientationStorage";

interface SimpleExportOptions {
  designId?: string;
  campaignId?: string;
  format?: string;
  useCustomTwoPageLayout?: boolean;
  returnHtml?: boolean;
  fileName?: string;
  editor?: any; // Optional editor instance to control camera
  skipCameraAdjustment?: boolean; // Skip camera changes during export (for auto-saves)
  keepCameraPosition?: boolean; // Keep camera centered after export (for manual saves/exports)
  displaySafetyAndCutLines?: boolean; // Show safety/cut line overlays in the export
}

// Import DocumentOrientation enum if not already available
enum DocumentOrientation {
  Portrait = "Portrait",
  Landscape = "Landscape",
}

// Global map to store forced canvas categories (outside tldraw store to avoid validation issues)
const forcedCanvasCategories = new Map<string, string>();

/**
 * Get the current orientation and calculate proper dimensions
 */
function getOrientationAndDimensions(format: string, designId?: string, campaignId?: string) {
  // Get stored orientation from localStorage using the new pattern with design/campaign ID
  // const { designId, campaignId } = extractIDsFromURL();
  const config = getEffectiveFormat(format);
  const storedOrientation = getStoredOrientation(
    designId,
    campaignId,
    config?.defaultOrientation ?? DocumentOrientation.Portrait
  );
  const isPortrait = storedOrientation === DocumentOrientation.Portrait;

  console.log("config", {
    format,
    config,
    storedOrientation,
    isPortrait,
    designId,
    campaignId,
  });
  if (!config) {
    throw new Error(`Format ${format} not found`);
  }
  const bleedPx = getExportBleedPx(config);

  // Some formats don't support landscape (TEARAWAY, DOOR_HANGER, or whatever the catalogue says)
  const isLandscapeEnabled = isRuntimeFormat(config)
    ? config.allowLandscape
    : format !== "TEARAWAY" && format !== "DOOR_HANGER";

  // Calculate actual dimensions based on orientation
  let actualWidthInMm, actualHeightInMm, actualDisplayWidth, actualDisplayHeight;

  // Detect the natural orientation of the format config
  const isNaturallyLandscape = config.displayWidth > config.displayHeight;

  if (isPortrait) {
    if (isNaturallyLandscape) {
      // Config is landscape but user wants portrait — swap to make height > width
      actualWidthInMm = config.heightInMm;
      actualHeightInMm = config.widthInMm;
      actualDisplayWidth = config.displayHeight;
      actualDisplayHeight = config.displayWidth;
    } else {
      // Config is already portrait
      actualWidthInMm = config.widthInMm;
      actualHeightInMm = config.heightInMm;
      actualDisplayWidth = config.displayWidth;
      actualDisplayHeight = config.displayHeight;
    }
  } else if (!isLandscapeEnabled) {
    // Landscape disabled for this format — use config as-is
    actualWidthInMm = config.widthInMm;
    actualHeightInMm = config.heightInMm;
    actualDisplayWidth = config.displayWidth;
    actualDisplayHeight = config.displayHeight;
  } else {
    // Landscape
    if (!isNaturallyLandscape) {
      // Config is portrait but user wants landscape — swap to make width > height
      actualWidthInMm = config.heightInMm;
      actualHeightInMm = config.widthInMm;
      actualDisplayWidth = config.displayHeight;
      actualDisplayHeight = config.displayWidth;
    } else {
      // Config is already landscape
      actualWidthInMm = config.widthInMm;
      actualHeightInMm = config.heightInMm;
      actualDisplayWidth = config.displayWidth;
      actualDisplayHeight = config.displayHeight;
    }
  }

  return {
    orientation: storedOrientation,
    isPortrait,
    isLandscapeEnabled,
    widthInMm: actualWidthInMm,
    heightInMm: actualHeightInMm,
    bleedPx,
    displayWidth: actualDisplayWidth + bleedPx * 2,
    displayHeight: actualDisplayHeight + bleedPx * 2,
  };
}

/**
 * Helper functions for font embedding
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

function getMimeTypeFromUrlOrFormat(url: string, format?: string, contentType?: string | null): string | null {
  if (contentType && contentType.startsWith("font/")) {
    return contentType;
  }

  if (format) {
    switch (format.toLowerCase()) {
      case "woff2":
        return "font/woff2";
      case "woff":
        return "font/woff";
      case "ttf":
      case "truetype":
        return "font/ttf";
      case "otf":
      case "opentype":
        return "font/otf";
      case "eot":
        return "application/vnd.ms-fontobject";
    }
  }

  const extension = url.split(".").pop()?.toLowerCase();
  switch (extension) {
    case "woff2":
      return "font/woff2";
    case "woff":
      return "font/woff";
    case "ttf":
      return "font/ttf";
    case "otf":
      return "font/otf";
    case "eot":
      return "application/vnd.ms-fontobject";
    default:
      return null;
  }
}

function extractUrlsFromSrc(srcValue: string): Array<{ url: string; format?: string }> {
  const urlRegex = /url\(\s*["']?([^"')]+)["']?\s*\)(?:\s+format\(\s*["']?([^"')]+)["']?\s*\))?/g;
  const urls: Array<{ url: string; format?: string }> = [];
  let match;

  while ((match = urlRegex.exec(srcValue)) !== null) {
    urls.push({
      url: match[1],
      format: match[2] || undefined,
    });
  }

  return urls;
}

async function embedSingleFontFace(fontFaceRule: CSSFontFaceRule): Promise<string> {
  const originalSrcValue = fontFaceRule.style.getPropertyValue("src");
  if (!originalSrcValue) {
    return fontFaceRule.cssText;
  }

  const urlsToProcess = extractUrlsFromSrc(originalSrcValue);
  if (urlsToProcess.length === 0) {
    return fontFaceRule.cssText;
  }

  for (const fontUrlObj of urlsToProcess) {
    try {
      const baseHref = fontFaceRule.parentStyleSheet?.href || document.baseURI;
      const absoluteUrl = new URL(fontUrlObj.url, baseHref).href;

      const response = await fetch(absoluteUrl, { mode: "cors" });

      if (!response.ok) {
        continue; // Try next URL in the list
      }

      const arrayBuffer = await response.arrayBuffer();
      const base64 = arrayBufferToBase64(arrayBuffer);
      const mimeType = getMimeTypeFromUrlOrFormat(absoluteUrl, fontUrlObj.format, response.headers.get("Content-Type"));

      if (mimeType) {
        let newRuleCss = `@font-face {\n`;
        for (let i = 0; i < fontFaceRule.style.length; i++) {
          const propName = fontFaceRule.style[i];
          const propValue = fontFaceRule.style.getPropertyValue(propName);
          const propPriority = fontFaceRule.style.getPropertyPriority(propName);
          const important = propPriority ? ` !${propPriority}` : "";

          if (propName.toLowerCase() === "src") {
            const embeddedSrc = `url(data:${mimeType};base64,${base64})${
              fontUrlObj.format ? ` format("${fontUrlObj.format}")` : ""
            }`;
            newRuleCss += `  src: ${embeddedSrc}${important};\n`;
          } else {
            newRuleCss += `  ${propName}: ${propValue}${important};\n`;
          }
        }
        newRuleCss += `}\n`;
        return newRuleCss; // Return the new rule with the first successfully embedded font
      }
    } catch (error: any) {
      continue; // Try next URL
    }
  }

  // If all URLs failed, return the original rule
  return fontFaceRule.cssText;
}

/**
 * Get all font families used in the design
 */
function getUsedFontFamilies(elementClone: HTMLElement): Set<string> {
  const usedFonts = new Set<string>();

  // Get all elements with font-family styles
  const allElements = [elementClone, ...Array.from(elementClone.querySelectorAll("*"))];

  for (const element of allElements) {
    const computedStyle = window.getComputedStyle(element);
    const fontFamily = computedStyle.fontFamily;

    if (fontFamily && fontFamily !== "inherit") {
      // Parse font-family value which can be comma-separated with quotes
      const fonts = fontFamily.split(",").map((font) => font.trim().replace(/['"]/g, "").toLowerCase());
      fonts.forEach((font) => {
        if (font && font !== "inherit" && font !== "initial" && font !== "unset") {
          usedFonts.add(font);
        }
      });
    }

    // Also check inline styles
    const inlineStyle = (element as HTMLElement).style.fontFamily;
    if (inlineStyle) {
      const fonts = inlineStyle.split(",").map((font) => font.trim().replace(/['"]/g, "").toLowerCase());
      fonts.forEach((font) => {
        if (font && font !== "inherit" && font !== "initial" && font !== "unset") {
          usedFonts.add(font);
        }
      });
    }
  }

  return usedFonts;
}

// Session-scoped caches for `embedGoogleFonts`. The function is called on
// every save (manual + autosave) by `generateAndSaveDesign`, and previously
// re-fetched every Google Fonts CSS chunk + every woff2 referenced inside
// — i.e. dozens of network requests per save. Both layers are
// deterministic: same `<link href>` always returns the same CSS, and same
// font URL always returns the same buffer. Caching in memory means only
// the first save in a session pays the network cost.
//
// Stored on `window` so they survive HMR / module re-evaluation, the same
// way `loadedHrefsCache` does in `useGoogleFonts.ts`.
type EmbedFontCaches = {
  css: Map<string, string>; // googleFontsCssUrl → cssText
  font: Map<string, string>; // fontFileUrl → data URL (with mime + base64)
};
const _embedFontCaches: EmbedFontCaches = (() => {
  if (typeof window === "undefined") {
    return { css: new Map(), font: new Map() };
  }
  const w = window as any;
  if (!w.__embedGoogleFontsCaches) {
    w.__embedGoogleFontsCaches = { css: new Map(), font: new Map() };
  }
  return w.__embedGoogleFontsCaches as EmbedFontCaches;
})();

/**
 * Embed fonts from Google Fonts link tags (only used fonts).
 * Memoizes CSS fetches per `<link href>` and font-file fetches per URL so
 * repeat saves don't re-issue identical network requests.
 */
async function embedGoogleFonts(usedFonts: Set<string>): Promise<string[]> {
  const embeddedFonts: string[] = [];

  // Find all Google Fonts link tags
  const googleFontLinks = Array.from(
    document.querySelectorAll('link[href*="fonts.googleapis.com"]')
  ) as HTMLLinkElement[];

  for (const link of googleFontLinks) {
    try {
      let cssText = _embedFontCaches.css.get(link.href);
      if (cssText === undefined) {
        const response = await fetch(link.href, {
          headers: {
            // Request WOFF2 format
            "User-Agent":
              "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36",
          },
        });

        if (!response.ok) continue;
        cssText = await response.text();
        _embedFontCaches.css.set(link.href, cssText);
      }

      // Parse @font-face rules and check if they're used
      const fontFaceRegex = /@font-face\s*{[^}]*}/g;
      let match;

      while ((match = fontFaceRegex.exec(cssText)) !== null) {
        const fontFaceRule = match[0];

        // Extract font-family from the rule
        const familyMatch = fontFaceRule.match(/font-family:\s*["']?([^"';]+)["']?/i);
        if (familyMatch) {
          const fontFamily = familyMatch[1].toLowerCase().trim();

          // Only process this font if it's actually used
          if (usedFonts.has(fontFamily)) {
            // Extract and embed font URLs from this specific @font-face rule
            const urlRegex = /url\(([^)]+)\)/g;
            let modifiedRule = fontFaceRule;
            let urlMatch;

            while ((urlMatch = urlRegex.exec(fontFaceRule)) !== null) {
              const fontUrl = urlMatch[1].replace(/['"]/g, "");
              try {
                let dataUrl = _embedFontCaches.font.get(fontUrl);
                if (dataUrl === undefined) {
                  const fontResponse = await fetch(fontUrl);
                  if (!fontResponse.ok) continue;
                  const fontBuffer = await fontResponse.arrayBuffer();
                  const base64Font = arrayBufferToBase64(fontBuffer);
                  const mimeType =
                    getMimeTypeFromUrlOrFormat(fontUrl, "", fontResponse.headers.get("Content-Type")) || "font/woff2";
                  dataUrl = `data:${mimeType};base64,${base64Font}`;
                  _embedFontCaches.font.set(fontUrl, dataUrl);
                }
                modifiedRule = modifiedRule.replace(fontUrl, dataUrl);
              } catch (fontError) {
                // Keep original URL if font can't be fetched
              }
            }

            embeddedFonts.push(modifiedRule);
          }
        }
      }
    } catch (error) {
      // Skip this Google Fonts link if it can't be processed
      continue;
    }
  }

  return embeddedFonts;
}

/**
 * Embed fonts from document.fonts API and get CDN font URLs (only used fonts)
 */
async function embedDocumentFonts(usedFonts: Set<string>): Promise<string[]> {
  const embeddedFonts: string[] = [];

  // Wait for all fonts to be loaded
  await document.fonts.ready;

  // Get custom fonts from S3 CDN and local storage
  const allCustomFontsData: Map<string, any> = new Map();

  // Try S3 CDN first (primary source)
  try {
    const { s3CustomFontService } = await import("../../services/s3CustomFontService");
    const s3FontsData = await s3CustomFontService.getFontCDNUrlsForExport(usedFonts);

    // Add S3 CDN fonts to the collection
    s3FontsData.forEach((fontData, fontName) => {
      allCustomFontsData.set(fontName, fontData);
    });

    console.log(`📦 Found ${s3FontsData.size} S3 CDN fonts for export`);
  } catch (error) {
    console.error("Could not load S3 CDN font URLs:", error);
  }

  // Try local storage as fallback (for backward compatibility)
  try {
    const { customFontStorage } = await import("../../services/customFontStorage");
    const localFontsData = await customFontStorage.getCustomFontsForExport();

    // Only add local fonts that aren't already loaded from S3 CDN
    localFontsData.forEach((fontData, fontName) => {
      if (!allCustomFontsData.has(fontName)) {
        allCustomFontsData.set(fontName, fontData);
      }
    });

    const localCount = localFontsData.size;
    console.log(`📦 Found ${localCount} additional local custom fonts for export`);
  } catch (error) {
    console.error("Could not load local custom fonts data:", error);
  }

  // Get all loaded FontFace objects
  const fontFaces = Array.from(document.fonts);

  for (const fontFace of fontFaces) {
    try {
      // Skip if font is not loaded
      if (fontFace.status !== "loaded") continue;

      // Check if this font is actually used in the design
      const family = fontFace.family.toLowerCase().replace(/['"]/g, "");
      if (!usedFonts.has(family)) continue;

      // Get font properties
      const weight = fontFace.weight || "normal";
      const style = fontFace.style || "normal";
      const stretch = fontFace.stretch || "normal";

      // Check if this is a custom font with stored data
      const customFontData = allCustomFontsData.get(family);

      if (customFontData) {
        console.log(`📦 Using CDN font: ${fontFace.family}`);

        // Check if this is an S3 CDN font or data URL font
        if (customFontData.s3Url) {
          if (customFontData.s3Url.startsWith("http")) {
            // S3 CDN font - use URL directly
            const cdnRule = `@font-face {
  font-family: "${fontFace.family}";
  font-weight: ${weight};
  font-style: ${style};
  font-stretch: ${stretch};
  src: url('${customFontData.s3Url}');
  font-display: swap;
}`;
            embeddedFonts.push(cdnRule);
            console.log(`📦 Added CDN font rule for: ${fontFace.family} -> ${customFontData.s3Url}`);
            continue; // Skip the regular font processing for CDN fonts
          } else if (customFontData.s3Url.startsWith("data:")) {
            // Development fallback with data URL stored in s3Url field
            const [header, data] = customFontData.s3Url.split(",");
            if (data) {
              let mimeType = customFontData.mimeType || "font/ttf";
              // Extract mime type from data URL header if not provided
              const mimeMatch = header.match(/data:([^;]+)/);
              if (mimeMatch && mimeMatch[1]) {
                mimeType = mimeMatch[1];
              }

              const embeddedRule = `@font-face {
  font-family: "${fontFace.family}";
  font-weight: ${weight};
  font-style: ${style};
  font-stretch: ${stretch};
  src: url(data:${mimeType};base64,${data});
  font-display: swap;
}`;
              embeddedFonts.push(embeddedRule);
              console.log(`📦 Added fallback embedded font rule for: ${fontFace.family}`);
              continue;
            }
          }
        } else if (customFontData.dataUrl && customFontData.dataUrl.startsWith("data:")) {
          // Local font with data URL - extract base64 data
          const [header, data] = customFontData.dataUrl.split(",");
          if (data) {
            let mimeType = customFontData.mimeType || "font/ttf";
            // Extract mime type from data URL header if not provided
            const mimeMatch = header.match(/data:([^;]+)/);
            if (mimeMatch && mimeMatch[1]) {
              mimeType = mimeMatch[1];
            }

            const embeddedRule = `@font-face {
  font-family: "${fontFace.family}";
  font-weight: ${weight};
  font-style: ${style};
  font-stretch: ${stretch};
  src: url(data:${mimeType};base64,${data});
  font-display: swap;
}`;
            embeddedFonts.push(embeddedRule);
            console.log(`📦 Added embedded font rule for: ${fontFace.family}`);
            continue; // Skip the regular font processing for local fonts
          }
        }
      }

      // Regular font processing (Google fonts, system fonts, etc.)
      // Try to get the font data from various sources
      let fontSrc = "";

      // Try to get source URL (this is not standard but some browsers expose it)
      if ((fontFace as any).src) {
        fontSrc = (fontFace as any).src;
      } else if ((fontFace as any).$$_url) {
        // Custom property used by some font loading libraries
        fontSrc = (fontFace as any).$$_url;
      }

      if (fontSrc && fontSrc.startsWith("http")) {
        try {
          const response = await fetch(fontSrc, { mode: "cors" });
          if (response.ok) {
            const arrayBuffer = await response.arrayBuffer();
            const base64 = arrayBufferToBase64(arrayBuffer);
            const mimeType =
              getMimeTypeFromUrlOrFormat(fontSrc, "", response.headers.get("Content-Type")) || "font/woff2";

            const embeddedRule = `@font-face {
  font-family: "${fontFace.family}";
  font-weight: ${weight};
  font-style: ${style};
  font-stretch: ${stretch};
  src: url(data:${mimeType};base64,${base64});
  font-display: swap;
}`;
            embeddedFonts.push(embeddedRule);
          }
        } catch (fetchError) {
          console.warn(`Could not fetch font ${fontFace.family} from ${fontSrc}:`, fetchError);
          // If we can't fetch the font, create a fallback rule
          const fallbackRule = `@font-face {
  font-family: "${fontFace.family}";
  font-weight: ${weight};
  font-style: ${style};
  font-stretch: ${stretch};
  src: local("${fontFace.family}");
  font-display: swap;
}`;
          embeddedFonts.push(fallbackRule);
        }
      } else {
        // Create a local font reference for fonts we can't embed
        const localRule = `@font-face {
  font-family: "${fontFace.family}";
  font-weight: ${weight};
  font-style: ${style};
  font-stretch: ${stretch};
  src: local("${fontFace.family}");
  font-display: swap;
}`;
        embeddedFonts.push(localRule);
      }
    } catch (error) {
      console.error(`Error processing font ${fontFace.family}:`, error);
      // Skip fonts that can't be processed
      continue;
    }
  }

  console.log(`✅ Embedded ${embeddedFonts.length} fonts for export (including custom fonts)`);
  return embeddedFonts;
}

/**
 * Generates proper two-page layout HTML for PDFs
 * Separates front and back page elements based on their positioning
 */
function generateTwoPageLayout(elementClone: HTMLElement, format: string): string {
  const BLEED = getExportBleedPx(getEffectiveFormat(format));
  // Get all shapes from the cloned element
  const allShapes = Array.from(elementClone.querySelectorAll(".tl-shape")) as HTMLElement[];

  // Filter duplicated shapes by ID to avoid processing the same shape twice
  const shapeMap = new Map<string, HTMLElement>();
  allShapes.forEach((shape) => {
    const shapeId = shape.getAttribute("data-shape-id") || "";
    if (shapeId && !shapeMap.has(shapeId)) {
      shapeMap.set(shapeId, shape);
    }
  });
  const filteredShapes = Array.from(shapeMap.values());

  // Find the black line shapes first to calculate proper offset and bounds (includes bleeds)
  let frontBlackLineX = 0;
  let backBlackLineX = 0;
  let frontBlackLineBounds: { x: number; y: number; width: number; height: number } | null = null;
  let backBlackLineBounds: { x: number; y: number; width: number; height: number } | null = null;
  let frontBlackLineFound = false;
  let backBlackLineFound = false;

  // Helper function to extract shape bounds from transform and style
  const getShapeBounds = (shape: HTMLElement) => {
    const transform = shape.style.transform;
    let x = 0,
      y = 0;
    let width = 0,
      height = 0;

    if (transform && transform.includes("matrix")) {
      const matrixMatch = transform.match(/matrix\([^,]+,\s*[^,]+,\s*[^,]+,\s*[^,]+,\s*([^,]+),\s*([^,)]+)/);
      if (matrixMatch) {
        x = parseFloat(matrixMatch[1]);
        y = parseFloat(matrixMatch[2]);
      }
    }

    // Try to get width/height from various possible sources
    const style = shape.style;
    if (style.width && style.height) {
      width = parseFloat(style.width.replace("px", ""));
      height = parseFloat(style.height.replace("px", ""));
    } else {
      // Fallback to getBoundingClientRect if available
      try {
        const rect = shape.getBoundingClientRect();
        width = rect.width;
        height = rect.height;
      } catch (e) {
        // Default dimensions if we can't get them
        width = 100;
        height = 100;
      }
    }

    return { x, y, width, height };
  };

  // First pass: find black line shapes and their positions/bounds (includes bleeds)
  filteredShapes.forEach((shape) => {
    const shapeId = shape.getAttribute("data-shape-id") || "";

    // Look for black line shapes specifically (these include bleeds)
    if (shapeId === "shape:frontBlackLine") {
      const bounds = getShapeBounds(shape);
      frontBlackLineX = bounds.x;
      frontBlackLineBounds = bounds;
      frontBlackLineFound = true;
    } else if (shapeId === "shape:backBlackLine") {
      const bounds = getShapeBounds(shape);
      backBlackLineX = bounds.x;
      backBlackLineBounds = bounds;
      backBlackLineFound = true;
    }
  });

  // Calculate offset based on black line shapes only
  // The front black line shape should be positioned at X=0 after offset
  const frontOffset = frontBlackLineFound && frontBlackLineX < 0 ? Math.abs(frontBlackLineX) : 0;

  // Helper function to check if two rectangles overlap
  const rectanglesOverlap = (
    rect1: { x: number; y: number; width: number; height: number },
    rect2: { x: number; y: number; width: number; height: number }
  ): boolean => {
    return !(
      rect1.x + rect1.width <= rect2.x ||
      rect2.x + rect2.width <= rect1.x ||
      rect1.y + rect1.height <= rect2.y ||
      rect2.y + rect2.height <= rect1.y
    );
  };

  // Helper function to calculate overlap area
  const getOverlapArea = (
    rect1: { x: number; y: number; width: number; height: number },
    rect2: { x: number; y: number; width: number; height: number }
  ): number => {
    if (!rectanglesOverlap(rect1, rect2)) return 0;

    const overlapWidth = Math.min(rect1.x + rect1.width, rect2.x + rect2.width) - Math.max(rect1.x, rect2.x);
    const overlapHeight = Math.min(rect1.y + rect1.height, rect2.y + rect2.height) - Math.max(rect1.y, rect2.y);

    return overlapWidth * overlapHeight;
  };

  // Separate content shapes into front and back based on overlap with format bounds
  const frontShapes: HTMLElement[] = [];
  const backShapes: HTMLElement[] = [];

  filteredShapes.forEach((shape) => {
    const shapeId = shape.getAttribute("data-shape-id") || "";

    let isBackPage = false;

    // 🆕 PRIORITY 1: Check for forced layer category from DOM
    const forcedCategory = shape.getAttribute("data-forced-layer-category");
    if (forcedCategory) {
      isBackPage = forcedCategory.toLowerCase() === "back";
    }
    // PRIORITY 2: Override with explicit front/back shape IDs
    else if (shapeId.includes("front") || shapeId.includes("Front")) {
      isBackPage = false;
    } else if (shapeId.includes("back") || shapeId.includes("Back")) {
      isBackPage = true;
    }
    // PRIORITY 3: Use overlap detection (fallback)
    else if (frontBlackLineBounds && backBlackLineBounds) {
      // Use overlap detection for content shapes
      const shapeBounds = getShapeBounds(shape);

      const frontOverlap = getOverlapArea(shapeBounds, frontBlackLineBounds);
      const backOverlap = getOverlapArea(shapeBounds, backBlackLineBounds);

      if (frontOverlap > 0 || backOverlap > 0) {
        // Assign to the page with more overlap
        isBackPage = backOverlap > frontOverlap;
      } else {
        // No overlap with either black line - use center point to decide
        const shapeCenterX = shapeBounds.x + shapeBounds.width / 2;
        const frontCenterX = frontBlackLineBounds.x + frontBlackLineBounds.width / 2;
        const backCenterX = backBlackLineBounds.x + backBlackLineBounds.width / 2;

        const distanceToFront = Math.abs(shapeCenterX - frontCenterX);
        const distanceToBack = Math.abs(shapeCenterX - backCenterX);

        isBackPage = distanceToBack < distanceToFront;
      }
    } else {
      // Fallback to simple X position check if black line bounds not found
      const transform = shape.style.transform;
      if (transform && transform.includes("matrix")) {
        const matrixMatch = transform.match(/matrix\([^,]+,\s*[^,]+,\s*[^,]+,\s*[^,]+,\s*([^,]+)/);
        if (matrixMatch) {
          const xPosition = parseFloat(matrixMatch[1]);
          if (frontBlackLineFound && backBlackLineFound) {
            const midPoint = (frontBlackLineX + backBlackLineX) / 2;
            isBackPage = xPosition > midPoint;
          } else {
            isBackPage = xPosition > 0;
          }
        }
      }
    }

    const shapeClone = shape.cloneNode(true) as HTMLElement;

    if (isBackPage) {
      backShapes.push(shapeClone);
    } else {
      frontShapes.push(shapeClone);
    }
  });

  // Create the two-page layout HTML with container-level transforms
  return `
    <div class="pages-container">
      <div class="page front-page" data-page="front">
        <div class="page-content front-content" style="transform: translate(${frontOffset}px, ${BLEED}px);">
          ${frontShapes.map((shape) => shape.outerHTML).join("")}
        </div>
      </div>
      
      <div class="page back-page" data-page="back">
        <div class="page-content back-content" style="transform: translate(${
          backBlackLineFound && backBlackLineX > 0 ? -backBlackLineX : 0
        }px, ${BLEED}px);">
          ${backShapes.map((shape) => shape.outerHTML).join("")}
        </div>
      </div>
    </div>
  `;
}

/**
 * Calculate default canvas positions based on format and orientation
 */
function getDefaultCanvasPositions(format: string, designId?: string, campaignId?: string) {
  const orientationInfo = getOrientationAndDimensions(format, designId, campaignId);

  const centerX = 0;
  const centerY = 0;
  const FIX_GAP = 1.65;
  const bleedsZonePx = getExportBleedZonePx(getEffectiveFormat(format));

  const displayWidth = orientationInfo.displayWidth - orientationInfo.bleedPx * 2; // Remove bleed from display
  const displayHeight = orientationInfo.displayHeight - orientationInfo.bleedPx * 2;

  return {
    frontBlackLine: {
      x: centerX - displayWidth / FIX_GAP - bleedsZonePx / 2,
      y: centerY - bleedsZonePx / 2,
    },
    backBlackLine: {
      x: centerX + displayWidth / FIX_GAP - bleedsZonePx / 2,
      y: centerY - bleedsZonePx / 2,
    },
  };
}

/**
 * Get all shapes that belong to a specific canvas (front or back)
 * Works with both new Tldraw Editor API and legacy TldrawApp
 */
function getCanvasShapes(editor: any, canvasType: "front" | "back"): any[] {
  let allShapes: any[] = [];
  let blackLine: any = null;
  const blackLineId = `shape:${canvasType}BlackLine`;

  // Check if this is new Tldraw Editor or legacy TldrawApp
  if (typeof editor.getCurrentPageShapes === "function") {
    // New Tldraw Editor API
    allShapes = editor.getCurrentPageShapes();
    blackLine = allShapes.find((s: any) => s.id === blackLineId);
  } else if (editor.state && editor.state.document) {
    // Legacy TldrawApp API
    const currentPageId = editor.state.appState?.currentPageId;
    if (currentPageId && editor.state.document.pages[currentPageId]) {
      const shapesObject = editor.state.document.pages[currentPageId].shapes;
      allShapes = Object.values(shapesObject);
      blackLine = shapesObject[blackLineId];
    }
  }

  if (!blackLine || allShapes.length === 0) return [];

  // Get bounds based on API version
  let canvasBounds: any;
  if (blackLine.x !== undefined) {
    // New API: shape has x, y directly
    canvasBounds = {
      minX: blackLine.x,
      maxX: blackLine.x + (blackLine.props?.w || 0),
      minY: blackLine.y,
      maxY: blackLine.y + (blackLine.props?.h || 0),
    };
  } else if (blackLine.point) {
    // Legacy API: shape has point [x, y]
    canvasBounds = {
      minX: blackLine.point[0],
      maxX: blackLine.point[0] + (blackLine.props?.w || blackLine.size?.[0] || 0),
      minY: blackLine.point[1],
      maxY: blackLine.point[1] + (blackLine.props?.h || blackLine.size?.[1] || 0),
    };
  } else {
    return [];
  }

  // Get all shapes that overlap with this canvas
  const shapesInCanvas = allShapes.filter((shape: any) => {
    // Skip the black lines themselves
    if (shape.id.includes("BlackLine")) return false;

    // Filter mask shapes, preview shapes and shapes with imageOffset
    // These are rendered as part of other shapes, not as independent elements
    const shapeMeta = shape.meta || {};
    const isMaskShape = (shapeMeta?.isMaskShape && shapeMeta?.isImageMask) || shapeMeta?.isCompositePart;
    const isPreviewShape = shapeMeta?.isPreviewShape === true;
    const hasImageOffset = !!(shapeMeta?.imageOffsetX || shapeMeta?.imageOffsetY);

    if (isMaskShape || isPreviewShape || hasImageOffset) {
      return false;
    }

    // 🆕 PRIORITY 1: Respect forcedLayerCategory (set before repositioning)
    const forcedCategory = forcedCanvasCategories.get(shape.id);
    if (forcedCategory) {
      return forcedCategory.toLowerCase() === canvasType.toLowerCase();
    }

    // PRIORITY 2: Check if shape ID explicitly indicates it belongs to this canvas
    const shapeId = shape.id.toLowerCase();
    if (shapeId.includes(canvasType.toLowerCase())) return true;

    // PRIORITY 3: Check by overlap (fallback for shapes without forced category)
    // Get shape position based on API version
    let shapeX, shapeY, shapeW, shapeH;

    // Special handling for draw and highlight shapes that use segments instead of w/h
    if (shape.type === "customDraw" || shape.type === "customHighlight") {
      // For draw/highlight shapes, we need to get bounds from geometry
      if (typeof editor.getShapeUtil === "function") {
        try {
          const shapeUtil = editor.getShapeUtil(shape);
          const geometry = shapeUtil.getGeometry(shape);
          const bounds = geometry.bounds;
          shapeX = shape.x !== undefined ? shape.x + bounds.x : shape.point ? shape.point[0] + bounds.x : 0;
          shapeY = shape.y !== undefined ? shape.y + bounds.y : shape.point ? shape.point[1] + bounds.y : 0;
          shapeW = bounds.w;
          shapeH = bounds.h;
        } catch (e) {
          // Fallback if geometry fails
          return false;
        }
      } else {
        // Legacy API fallback - skip these shapes if we can't get proper bounds
        return false;
      }
    } else if (shape.x !== undefined) {
      // New API
      shapeX = shape.x;
      shapeY = shape.y;
      shapeW = shape.props?.w || 0;
      shapeH = shape.props?.h || 0;
    } else if (shape.point) {
      // Legacy API
      shapeX = shape.point[0];
      shapeY = shape.point[1];
      shapeW = shape.props?.w || shape.size?.[0] || 0;
      shapeH = shape.props?.h || shape.size?.[1] || 0;
    } else {
      return false;
    }

    // Check if shape overlaps with canvas bounds
    const shapeMaxX = shapeX + shapeW;
    const shapeMaxY = shapeY + shapeH;

    const overlaps = !(
      shapeMaxX < canvasBounds.minX ||
      shapeX > canvasBounds.maxX ||
      shapeMaxY < canvasBounds.minY ||
      shapeY > canvasBounds.maxY
    );

    return overlaps;
  });

  return shapesInCanvas;
}

/**
 * Assign forcedLayerCategory to all shapes based on current canvas overlap
 * This "freezes" the categorization before repositioning canvas
 * Stores categories in a Map instead of shape props to avoid tldraw validation errors
 * Returns a function to clear the forced categories
 */
async function assignForcedCanvasCategories(editor: any): Promise<(() => void) | null> {
  try {
    const hasNewAPI = typeof editor.getCurrentPageShapes === "function";

    if (!hasNewAPI) {
      return null; // No-op restore function
    }

    const allShapes = editor.getCurrentPageShapes();
    const frontBlackLine = editor.getShape("shape:frontBlackLine" as any);
    const backBlackLine = editor.getShape("shape:backBlackLine" as any);

    if (!frontBlackLine || !backBlackLine) {
      return null;
    }

    // Calculate bounds for both canvas
    const frontBounds = {
      minX: frontBlackLine.x,
      maxX: frontBlackLine.x + (frontBlackLine.props as any).w,
      minY: frontBlackLine.y,
      maxY: frontBlackLine.y + (frontBlackLine.props as any).h,
    };

    const backBounds = {
      minX: backBlackLine.x,
      maxX: backBlackLine.x + (backBlackLine.props as any).w,
      minY: backBlackLine.y,
      maxY: backBlackLine.y + (backBlackLine.props as any).h,
    };

    // Helper to check overlap
    const checkOverlap = (shape: any, bounds: any) => {
      let shapeBounds;

      // Special handling for draw and highlight shapes that use segments instead of w/h
      if (shape.type === "customDraw" || shape.type === "customHighlight") {
        try {
          const shapeUtil = editor.getShapeUtil(shape);
          const geometry = shapeUtil.getGeometry(shape);
          const geomBounds = geometry.bounds;
          shapeBounds = {
            x: shape.x + geomBounds.x,
            y: shape.y + geomBounds.y,
            width: geomBounds.w,
            height: geomBounds.h,
          };
        } catch (e) {
          return false;
        }
      } else {
        // Standard shapes with w/h props
        shapeBounds = {
          x: shape.x,
          y: shape.y,
          width: (shape.props as any)?.w || 0,
          height: (shape.props as any)?.h || 0,
        };
      }

      return !(
        shapeBounds.x + shapeBounds.width < bounds.minX ||
        shapeBounds.x > bounds.maxX ||
        shapeBounds.y + shapeBounds.height < bounds.minY ||
        shapeBounds.y > bounds.maxY
      );
    };

    // Clear any existing forced categories
    forcedCanvasCategories.clear();

    allShapes.forEach((shape: any) => {
      // Skip canvas base shapes
      if (
        shape.id.includes("BlackLine") ||
        shape.id.includes("RedLine") ||
        shape.id.includes("FoldLine") ||
        shape.id.includes("BorderDashLine") ||
        shape.id.includes("Label") ||
        shape.id === "shape:front" ||
        shape.id === "shape:back"
      ) {
        return;
      }

      // 🆕 ALWAYS determine canvas based on CURRENT position (no skip)
      // This ensures shapes moved between canvas are correctly categorized
      const overlapsFront = checkOverlap(shape, frontBounds);
      const overlapsBack = checkOverlap(shape, backBounds);

      let category: string | undefined;
      if (overlapsFront && !overlapsBack) {
        category = "front";
      } else if (overlapsBack && !overlapsFront) {
        category = "back";
      } else if (overlapsFront && overlapsBack) {
        // If overlaps both, prioritize front
        category = "front";
      }
      // If overlaps neither, don't assign category (stays undefined)

      // Store category in the global Map (not in shape props to avoid validation errors)
      if (category) {
        forcedCanvasCategories.set(shape.id, category);
      }
    });

    // Log and add data attributes to DOM elements for debugging
    if (forcedCanvasCategories.size > 0) {
      // Add data-forced-layer-category attribute to DOM elements
      await new Promise((resolve) => setTimeout(resolve, 50)); // Wait for DOM update

      forcedCanvasCategories.forEach((category, shapeId) => {
        const domElement = document.querySelector(`[data-shape-id="${shapeId}"]`);
        if (domElement) {
          domElement.setAttribute("data-forced-layer-category", category);
        }
      });
    }

    // Return cleanup function to clear the forced categories map and DOM attributes
    return () => {
      // Remove DOM attributes
      forcedCanvasCategories.forEach((_, shapeId) => {
        const domElement = document.querySelector(`[data-shape-id="${shapeId}"]`);
        if (domElement) {
          domElement.removeAttribute("data-forced-layer-category");
        }
      });

      // Clear the map
      forcedCanvasCategories.clear();
    };
  } catch (error) {
    console.error("❌ Error assigning forced canvas categories:", error);
    return null;
  }
}

/**
 * Temporarily move canvas shapes to default positions for export
 * Returns a function to restore original positions
 */
function temporarilyResetCanvasPositions(
  editor: any,
  format: string,
  designId?: string,
  campaignId?: string
): (() => void) | null {
  try {
    // Get default positions
    const defaultPositions = getDefaultCanvasPositions(format, designId, campaignId);

    // Get current positions - support both APIs
    let frontBlackLine: any = null;
    let backBlackLine: any = null;

    // Check which API we're using - be more precise
    const hasNewAPI = typeof editor.getCurrentPageShapes === "function";
    const hasLegacyAPI = editor.state && editor.state.document && editor.state.document.pages;

    if (hasNewAPI) {
      // New Tldraw Editor API
      frontBlackLine = editor.getShape("shape:frontBlackLine" as any);
      backBlackLine = editor.getShape("shape:backBlackLine" as any);
    } else if (hasLegacyAPI) {
      // Legacy TldrawApp API
      const currentPageId = editor.state.appState?.currentPageId;

      if (currentPageId && editor.state.document.pages[currentPageId]) {
        const shapes = editor.state.document.pages[currentPageId].shapes;
        frontBlackLine = shapes["shape:frontBlackLine"];
        backBlackLine = shapes["shape:backBlackLine"];
      }
    }

    if (!frontBlackLine || !backBlackLine) {
      return null;
    }

    // Get positions based on API version
    let currentPositions: any;
    if (frontBlackLine.x !== undefined) {
      // New API
      currentPositions = {
        frontBlackLine: { x: frontBlackLine.x, y: frontBlackLine.y },
        backBlackLine: { x: backBlackLine.x, y: backBlackLine.y },
      };
    } else if (frontBlackLine.point) {
      // Legacy API
      currentPositions = {
        frontBlackLine: { x: frontBlackLine.point[0], y: frontBlackLine.point[1] },
        backBlackLine: { x: backBlackLine.point[0], y: backBlackLine.point[1] },
      };
    } else {
      return null;
    }

    // Calculate deltas
    const frontDelta = {
      x: defaultPositions.frontBlackLine.x - currentPositions.frontBlackLine.x,
      y: defaultPositions.frontBlackLine.y - currentPositions.frontBlackLine.y,
    };

    const backDelta = {
      x: defaultPositions.backBlackLine.x - currentPositions.backBlackLine.x,
      y: defaultPositions.backBlackLine.y - currentPositions.backBlackLine.y,
    };

    // Check if canvas has been moved (delta is significant)
    const hasBeenMoved =
      Math.abs(frontDelta.x) > 0.1 ||
      Math.abs(frontDelta.y) > 0.1 ||
      Math.abs(backDelta.x) > 0.1 ||
      Math.abs(backDelta.y) > 0.1;

    if (!hasBeenMoved) {
      return null;
    }

    // Get all shapes for both canvas
    const frontShapes = getCanvasShapes(editor, "front");
    const backShapes = getCanvasShapes(editor, "back");

    // Also include the black line shapes themselves
    frontShapes.push(frontBlackLine);
    backShapes.push(backBlackLine);

    // 💾 Save original positions before moving
    const originalPositions = new Map<string, { x: number; y: number }>();
    [...frontShapes, ...backShapes].forEach((shape: any) => {
      if (shape.x !== undefined) {
        // New API
        originalPositions.set(shape.id, { x: shape.x, y: shape.y });
      } else if (shape.point) {
        // Legacy API
        originalPositions.set(shape.id, { x: shape.point[0], y: shape.point[1] });
      }
    });

    // Check if we're using new or legacy API (reuse detection from above)
    // const isNewAPI and isLegacyAPI already defined above, but in different scope
    // Redefine here to match the same logic
    const isNewAPIForShapeUpdate = typeof editor.getCurrentPageShapes === "function";
    const isLegacyAPIForShapeUpdate =
      !isNewAPIForShapeUpdate && editor.state && editor.state.document && editor.state.document.pages;

    if (isNewAPIForShapeUpdate) {
      // Move all shapes to default positions - New API
      editor.batch(() => {
        // Temporarily unlock shapes if needed
        const lockedShapes = [...frontShapes, ...backShapes].filter((s: any) => s.isLocked);

        if (lockedShapes.length > 0) {
          editor.updateShapes(
            lockedShapes.map((s: any) => ({
              id: s.id,
              type: s.type,
              isLocked: false,
            }))
          );
        }

        // Move front canvas shapes
        editor.updateShapes(
          frontShapes.map((s: any) => ({
            id: s.id,
            type: s.type,
            x: s.x + frontDelta.x,
            y: s.y + frontDelta.y,
          }))
        );

        // Move back canvas shapes
        editor.updateShapes(
          backShapes.map((s: any) => ({
            id: s.id,
            type: s.type,
            x: s.x + backDelta.x,
            y: s.y + backDelta.y,
          }))
        );

        // Re-lock shapes
        if (lockedShapes.length > 0) {
          editor.updateShapes(
            lockedShapes.map((s: any) => ({
              id: s.id,
              type: s.type,
              isLocked: true,
            }))
          );
        }
      });
    } else if (isLegacyAPIForShapeUpdate) {
      // Move all shapes to default positions - Legacy API
      const currentPageId = editor.state.appState?.currentPageId;
      if (!currentPageId) {
        console.warn("⚠️ Could not find current page ID");
        return null;
      }

      const allShapesToMove = [...frontShapes, ...backShapes];
      const updates: any = {};

      allShapesToMove.forEach((shape: any) => {
        const isInFront = frontShapes.includes(shape);
        const delta = isInFront ? frontDelta : backDelta;

        updates[shape.id] = {
          ...shape,
          point: [shape.point[0] + delta.x, shape.point[1] + delta.y],
        };
      });

      // Update state with all changes
      editor.setState((state: any) => {
        const newPages = { ...state.document.pages };
        const currentPage = { ...newPages[currentPageId] };
        currentPage.shapes = {
          ...currentPage.shapes,
          ...updates,
        };
        newPages[currentPageId] = currentPage;

        return {
          ...state,
          document: {
            ...state.document,
            pages: newPages,
          },
        };
      });
    } else {
      console.warn("⚠️ Unknown editor API");
      return null;
    }

    // Return restore function
    return () => {
      if (isNewAPIForShapeUpdate) {
        // Restore using New API
        editor.batch(() => {
          // Unlock shapes again
          const lockedShapes = [...frontShapes, ...backShapes].filter((s: any) => s.isLocked);

          if (lockedShapes.length > 0) {
            editor.updateShapes(
              lockedShapes.map((s: any) => ({
                id: s.id,
                type: s.type,
                isLocked: false,
              }))
            );
          }

          // Restore to original positions (directly from saved positions)
          const allShapesToRestore = [...frontShapes, ...backShapes];
          editor.updateShapes(
            allShapesToRestore
              .filter((s: any) => originalPositions.has(s.id))
              .map((s: any) => {
                const originalPos = originalPositions.get(s.id)!;
                return {
                  id: s.id,
                  type: s.type,
                  x: originalPos.x,
                  y: originalPos.y,
                };
              })
          );

          // Re-lock shapes
          if (lockedShapes.length > 0) {
            editor.updateShapes(
              lockedShapes.map((s: any) => ({
                id: s.id,
                type: s.type,
                isLocked: true,
              }))
            );
          }
        });
      } else if (isLegacyAPIForShapeUpdate) {
        // Restore using Legacy API
        const currentPageId = editor.state.appState?.currentPageId;
        if (!currentPageId) {
          console.warn("⚠️ Could not find current page ID for restore");
          return;
        }

        const allShapesToRestore = [...frontShapes, ...backShapes];
        const updates: any = {};

        allShapesToRestore.forEach((shape: any) => {
          const originalPos = originalPositions.get(shape.id);
          if (originalPos) {
            updates[shape.id] = {
              ...shape,
              point: [originalPos.x, originalPos.y], // Restore to original position
            };
          }
        });

        // Update state with all changes
        editor.setState((state: any) => {
          const newPages = { ...state.document.pages };
          const currentPage = { ...newPages[currentPageId] };
          currentPage.shapes = {
            ...currentPage.shapes,
            ...updates,
          };
          newPages[currentPageId] = currentPage;

          return {
            ...state,
            document: {
              ...state.document,
              pages: newPages,
            },
          };
        });
      }
    };
  } catch (error) {
    console.error("❌ Error resetting canvas positions:", error);
    return null;
  }
}

/**
 * Center the camera to show both front and back canvas
 * This is the same logic used during export, extracted for reuse
 */
export function centerCameraOnBothCanvas(editor: any) {
  try {
    // Prioritize window.tldrawEditor (new API) over passed editor (may be legacy)
    const tldrawEditor = (window as any).tldrawEditor || editor;
    if (!tldrawEditor) {
      console.warn("⚠️ Tldraw editor not found");
      return;
    }

    // Check if this is new Tldraw Editor or legacy TldrawApp
    if (typeof tldrawEditor.getCurrentPageShapes === "function") {
      // New Tldraw Editor API
      const allShapes = tldrawEditor.getCurrentPageShapes();

      // Find front and back canvas shapes
      const frontShape = allShapes.find((shape: any) => shape.id === "shape:frontBlackLine");
      const backShape = allShapes.find((shape: any) => shape.id === "shape:backBlackLine");

      if (frontShape && backShape) {
        // Use zoomToFit for more reliable centering after position changes
        tldrawEditor.zoomToFit({
          animation: { duration: 300 },
          inset: 50,
        });
      } else {
        // Fallback to standard zoomToFit if front/back shapes not found
        tldrawEditor.zoomToFit({
          animation: { duration: 300 },
          inset: 50,
        });

        // Center the view
        const pageBounds = tldrawEditor.getCurrentPageBounds();
        if (pageBounds) {
          const centerX = (pageBounds.minX + pageBounds.maxX) / 2;
          const centerY = (pageBounds.minY + pageBounds.maxY) / 2;
          tldrawEditor.centerOnPoint({ x: centerX, y: centerY });
        }
      }
    } else if (tldrawEditor.state && typeof tldrawEditor.zoomToFit === "function") {
      // Legacy TldrawApp API
      const currentPageId = tldrawEditor.state.appState?.currentPageId;
      if (currentPageId && tldrawEditor.state.document.pages[currentPageId]) {
        const shapes = tldrawEditor.state.document.pages[currentPageId].shapes;

        const frontShape = shapes["shape:frontBlackLine"];
        const backShape = shapes["shape:backBlackLine"];

        if (frontShape && backShape) {
          // Calculate the bounds of both front and back shapes
          const frontLeft = frontShape.point[0];
          const frontWidth = frontShape.props?.w || frontShape.size?.[0] || 0;
          const frontHeight = frontShape.props?.h || frontShape.size?.[1] || 0;
          const frontRight = frontLeft + frontWidth;

          const backLeft = backShape.point[0];
          const backWidth = backShape.props?.w || backShape.size?.[0] || 0;
          const backHeight = backShape.props?.h || backShape.size?.[1] || 0;
          const backRight = backLeft + backWidth;

          const totalLeft = Math.min(frontLeft, backLeft);
          const totalRight = Math.max(frontRight, backRight);
          const totalWidth = totalRight - totalLeft;
          const totalHeight = Math.max(frontHeight, backHeight);

          const boundsCenter = [totalLeft + totalWidth / 2, frontShape.point[1] + totalHeight / 2];

          const rendererBounds = tldrawEditor.rendererBounds;
          const padding = 100;

          const zoomX = (rendererBounds.width - padding) / totalWidth;
          const zoomY = (rendererBounds.height - padding) / totalHeight;
          const zoom = Math.min(zoomX, zoomY, 1);

          const cameraPoint = [
            boundsCenter[0] - rendererBounds.width / (2 * zoom),
            boundsCenter[1] - rendererBounds.height / (2 * zoom),
          ];

          tldrawEditor.setCamera(cameraPoint, zoom, "center_both_canvas");
        } else {
          tldrawEditor.zoomToFit();
        }
      }
    }
  } catch (error) {
    console.warn("⚠️ Failed to center camera:", error);
  }
}

/**
 * Export the entire Tldraw canvas to HTML by capturing the DOM directly
 * This preserves all fonts, styles, and elements exactly as they appear
 */
export async function simpleHtmlExport({
  format = "A5",
  useCustomTwoPageLayout = true,
  returnHtml = false,
  fileName = "tldraw-export.html",
  editor = null,
  designId,
  campaignId,
  skipCameraAdjustment = false,
  keepCameraPosition = false,
  displaySafetyAndCutLines = false,
}: SimpleExportOptions): Promise<string | void> {
  try {
    // Get orientation and calculate proper dimensions
    const orientationInfo = getOrientationAndDimensions(format, designId, campaignId);

    // 🎯 Save current camera state before export
    let originalCamera: { x: number; y: number; z: number } | null = null;

    // 🎯 NEW: Save function to restore canvas positions
    let restoreCanvasPositions: (() => void) | null = null;

    // 🎯 NEW: Save function to restore forced categories
    let restoreForcedCategories: (() => void) | null = null;

    // Prioritize window.tldrawEditor (new API) over passed editor (may be legacy)
    const tldrawEditor = (window as any).tldrawEditor || editor;

    // 🎬 If keepCameraPosition, do visual centering FIRST (before capture)
    if (keepCameraPosition) {
      try {
        // IMPORTANT: Use the global NEW editor, not the legacy one
        const globalEditor = (window as any).tldrawEditor;
        if (globalEditor) {
          centerCameraOnBothCanvas(globalEditor);
          // Wait for animation to complete + extra buffer for DOM update
          // 300ms animation + 200ms for DOM/render to settle
          await new Promise((resolve) => setTimeout(resolve, 500));
        } else {
          console.warn("⚠️ Global tldrawEditor not found for visual centering");
        }
      } catch (error) {
        console.warn("⚠️ Failed to do initial visual centering:", error);
      }
    }

    // ⚠️ Skip camera adjustments during auto-saves to prevent editor re-mounts
    // Also skip if keepCameraPosition is true (visual centering already done above)
    if (tldrawEditor && !skipCameraAdjustment && !keepCameraPosition) {
      try {
        // Save current camera state
        if (typeof tldrawEditor.getCamera === "function") {
          // New Tldraw Editor API
          originalCamera = tldrawEditor.getCamera();
        } else if (tldrawEditor.state?.appState?.currentPageId && tldrawEditor.state.document.pageStates) {
          // Legacy TldrawApp API
          const currentPageId = tldrawEditor.state.appState.currentPageId;
          const pageState = tldrawEditor.state.document.pageStates[currentPageId];
          if (pageState?.camera) {
            originalCamera = {
              x: pageState.camera.point[0],
              y: pageState.camera.point[1],
              z: pageState.camera.zoom,
            };
          }
        }

        // Check if this is new Tldraw Editor or legacy TldrawApp
        if (typeof tldrawEditor.getCurrentPageShapes === "function") {
          // New Tldraw Editor API
          const allShapes = tldrawEditor.getCurrentPageShapes();

          // 🔧 WORKAROUND: Check for FIX_GAP positioning issue in new API too
          const frontShape = allShapes.find((shape: any) => shape.id === "shape:frontBlackLine");
          const backShape = allShapes.find((shape: any) => shape.id === "shape:backBlackLine");

          if (frontShape && backShape) {
            // For new API, calculate bounds manually to ensure both pages are visible
            const frontBounds = tldrawEditor.getShapeGeometry(frontShape.id).bounds;
            const backBounds = tldrawEditor.getShapeGeometry(backShape.id).bounds;

            const totalLeft = Math.min(frontShape.x + frontBounds.x, backShape.x + backBounds.x);
            const totalRight = Math.max(
              frontShape.x + frontBounds.x + frontBounds.w,
              backShape.x + backBounds.x + backBounds.w
            );
            const totalTop = Math.min(frontShape.y + frontBounds.y, backShape.y + backBounds.y);
            const totalBottom = Math.max(
              frontShape.y + frontBounds.y + frontBounds.h,
              backShape.y + backBounds.y + backBounds.h
            );

            const customBounds = {
              x: totalLeft,
              y: totalTop,
              w: totalRight - totalLeft,
              h: totalBottom - totalTop,
            };

            // Zoom to fit the custom bounds
            tldrawEditor.zoomToBounds(customBounds, {
              animation: { duration: 0 },
              inset: 50,
            });
          } else {
            // Fallback to standard zoomToFit if front/back shapes not found
            tldrawEditor.zoomToFit({
              animation: { duration: 0 },
              inset: 50, // Add some padding around the content
            });

            // Center the view to ensure nothing is clipped
            const pageBounds = tldrawEditor.getCurrentPageBounds();
            if (pageBounds) {
              const centerX = (pageBounds.minX + pageBounds.maxX) / 2;
              const centerY = (pageBounds.minY + pageBounds.maxY) / 2;
              tldrawEditor.centerOnPoint({ x: centerX, y: centerY });
            }
          }
        } else if (tldrawEditor.state && typeof tldrawEditor.zoomToFit === "function") {
          // Legacy TldrawApp API
          const currentPageId = tldrawEditor.state.appState?.currentPageId;
          if (currentPageId && tldrawEditor.state.document.pages[currentPageId]) {
            const shapes = tldrawEditor.state.document.pages[currentPageId].shapes;

            // 🔧 WORKAROUND: Handle FIX_GAP positioning issue
            // The front page is positioned at centerX - displayWidth / FIX_GAP which can cause clipping
            const frontShape = shapes["shape:frontBlackLine"];
            const backShape = shapes["shape:backBlackLine"];

            if (frontShape && backShape) {
              // Calculate the bounds of both front and back shapes
              // TldrawApp shapes have point [x, y] and props.w, props.h
              const frontLeft = frontShape.point[0];
              const frontWidth = frontShape.props?.w || frontShape.size?.[0] || 0;
              const frontHeight = frontShape.props?.h || frontShape.size?.[1] || 0;
              const frontRight = frontLeft + frontWidth;

              const backLeft = backShape.point[0];
              const backWidth = backShape.props?.w || backShape.size?.[0] || 0;
              const backHeight = backShape.props?.h || backShape.size?.[1] || 0;
              const backRight = backLeft + backWidth;

              // Find the leftmost and rightmost edges
              const totalLeft = Math.min(frontLeft, backLeft);
              const totalRight = Math.max(frontRight, backRight);
              const totalWidth = totalRight - totalLeft;
              const totalHeight = Math.max(frontHeight, backHeight);

              // Calculate center point that includes both pages
              const boundsCenter = [totalLeft + totalWidth / 2, frontShape.point[1] + totalHeight / 2];

              // Calculate zoom to fit both pages with padding
              const rendererBounds = tldrawEditor.rendererBounds;
              const padding = 100; // Add some padding

              const zoomX = (rendererBounds.width - padding) / totalWidth;
              const zoomY = (rendererBounds.height - padding) / totalHeight;
              const zoom = Math.min(zoomX, zoomY, 1); // Don't zoom in more than 100%

              // Set camera to show both pages properly
              const cameraPoint = [
                boundsCenter[0] - rendererBounds.width / (2 * zoom),
                boundsCenter[1] - rendererBounds.height / (2 * zoom),
              ];

              tldrawEditor.setCamera(cameraPoint, zoom, "export_positioning_fix");
            } else {
              // Fallback to normal zoomToFit if front/back shapes not found
              tldrawEditor.zoomToFit();
            }
          }
        } else {
          console.warn("⚠️ Unknown editor type - cannot adjust camera");
        }

        // Give the UI a moment to update after camera changes
        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch (cameraError) {
        console.warn("⚠️ Could not adjust camera position:", cameraError);
      }
    } else {
      console.warn("⚠️ Tldraw editor not found on window object");
    }

    // 🆕 STEP 1: Assign forcedLayerCategory BEFORE moving canvas
    // This "freezes" which canvas each shape belongs to based on current positions
    if (tldrawEditor && !skipCameraAdjustment) {
      restoreForcedCategories = await assignForcedCanvasCategories(tldrawEditor);

      // Give the UI a moment to update after assigning categories
      if (restoreForcedCategories) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
    }

    // 🆕 STEP 2: Temporarily reset canvas positions to default before capture
    // Only reset positions on manual exports, not on auto-saves
    // Now shapes will maintain their canvas assignment thanks to forcedLayerCategory
    if (tldrawEditor && !skipCameraAdjustment) {
      restoreCanvasPositions = temporarilyResetCanvasPositions(tldrawEditor, format, designId, campaignId);

      // Give the UI a moment to update after moving shapes
      if (restoreCanvasPositions) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }

    // Find the Tldraw canvas element
    const canvasElement = document.querySelector(".tl-html-layer") as HTMLElement;

    if (!canvasElement) {
      console.error("❌ Could not find Tldraw canvas element (.tl-html-layer)");
      throw new Error("Could not find Tldraw canvas element (.tl-html-layer)");
    }

    // Clone the entire canvas element to capture all content
    const elementClone = canvasElement.cloneNode(true) as HTMLElement;

    // Clean up the clone for export
    elementClone.style.transform = "unset";
    elementClone.style.contain = "unset";

    // Remove any blacklisted elements that shouldn't be in the export
    const blackListedSelectors = [
      "script",
      "[data-shape-type='customLabel']",
      "[data-shape-id='shape:frontLabel']",
      "[data-shape-id='shape:backLabel']",
      // Optimistic QR color tint overlay — preview only; the exported
      // <img> already carries the real regenerated QR color.
      "[data-qr-tint-overlay]",
      // Selection and UI indicators
      ".tl-selection__fg",
      ".tl-selection__bg",
      ".tl-user-handles",
      ".tl-user-brush",
      ".tl-user-scribble",
      ".tl-user-snapline",
      ".tl-user-indicator__selected",
      ".tl-user-indicator__hovered",
      ".tl-user-indicator__hint",
      ".tl-collaborator__scribble",
      ".tl-collaborator__brush",
      ".tl-collaborator__shape-indicator",
      ".tl-collaborator__cursor",
      ".tl-collaborator__cursor-hint",
      ".tl-shape-indicator",
      ".tl-corner-handle",
      ".tl-binding-indicator",
      // Any element with selection-related data attributes
      '[data-testid*="selection"]',
      '[data-testid*="indicator"]',
      '[data-testid*="handle"]',
      '[class*="selection"]',
      '[class*="indicator"]',
      '[class*="handle"]',
    ];

    blackListedSelectors.forEach((selector) => {
      const elements = elementClone.querySelectorAll(selector);
      elements.forEach((el) => el.remove());
    });

    // Fix outside stroke clipping for shapes that use ShapeUtilHelper
    const shapesWithOutsideStroke = [
      "customEllipse",
      "customTriangle",
      "customOval",
      "customStar",
      "customCloud",
      "customHeart",
    ];

    shapesWithOutsideStroke.forEach((shapeType) => {
      const shapes = elementClone.querySelectorAll(`[data-shape-type="${shapeType}"]`);
      shapes.forEach((shapeElement) => {
        try {
          // Find all divs and SVGs within this shape
          const allDivs = shapeElement.querySelectorAll("div");
          const allSvgs = shapeElement.querySelectorAll("svg");

          // Apply overflow: visible to all divs and SVGs to prevent clipping
          allDivs.forEach((div) => {
            const divEl = div as HTMLElement;
            divEl.style.overflow = "visible";
            divEl.style.contain = "none";
          });

          allSvgs.forEach((svg) => {
            const svgEl = svg as SVGElement;
            svgEl.style.overflow = "visible";
            // Remove any clip-path that might be applied by tldraw
            svgEl.style.clipPath = "none";
          });
        } catch (error) {
          console.warn(`Failed to fix outside stroke for ${shapeType}:`, error);
        }
      });
    });

    // Restore export placeholders: elements with data-export-value show the actual text in the
    // canvas for preview, but the exported HTML must contain the placeholder token so the
    // print-time substitution pipeline can replace it correctly.
    // NOTE: data-export-value elements must be leaf/text-only nodes — textContent replaces the entire subtree.
    elementClone.querySelectorAll("[data-export-value]").forEach((el) => {
      const exportValue = el.getAttribute("data-export-value");
      if (exportValue !== null) {
        el.textContent = exportValue;
      }
    });

    // Remove safety/cut line guides unless explicitly requested for export
    if (!displaySafetyAndCutLines) {
      const safetyAndCutShapeIds = [
        "shape:frontBorderDashLine",
        "shape:backBorderDashLine",
        "shape:frontRedLine",
        "shape:backRedLine",
        "shape:diecutLineFront",
        "shape:diecutLineBack",
        "shape:cutLineFront",
        "shape:cutLineBack",
        "shape:a6SafetyLineFront",
        "shape:a6SafetyLineBack",
        "shape:TearawayCutLineFront",
        "shape:TearawayCutLineBack",
        "shape:tearawayFlyerBuilderCutlineFront",
        "shape:tearawayFlyerBuilderCutlineBack",
        "shape:tearawaySafetyLineFront",
        "shape:tearawaySafetyLineBack",
      ];

      safetyAndCutShapeIds.forEach((shapeId) => {
        elementClone.querySelectorAll(`[data-shape-id='${shapeId}']`).forEach((el) => el.remove());
      });
      // Fold guides are drawn per side with the same visibility rule as the cut and safety lines
      elementClone.querySelectorAll("[data-shape-type='foldLine']").forEach((el) => el.remove());
    }

    // Note: Removed offset calculation to allow proper clipping of overflowing elements
    // Elements that overflow on the left should be clipped, not cause the design to shift

    // Analyze which fonts are actually used in the design
    const usedFonts = getUsedFontFamilies(elementClone);

    // Get all CSS styles from the page and embed fonts
    const regularCssTexts: string[] = [];
    const fontFaceProcessingPromises: Promise<string>[] = [];
    const styleSheets = Array.from(document.styleSheets) as CSSStyleSheet[];

    styleSheets.forEach((sheet) => {
      let cssRules: CSSRuleList | null = null;
      try {
        cssRules = sheet.rules || sheet.cssRules;
      } catch (e: any) {
        if (sheet.href) {
          regularCssTexts.push(`\n/* CSS rules from ${sheet.href} could not be accessed due to CORS restrictions */\n`);
        }
        return;
      }

      if (cssRules) {
        Array.from(cssRules).forEach((rule) => {
          if (rule instanceof CSSFontFaceRule) {
            // Check if this font is used before processing
            const fontFamily = rule.style.getPropertyValue("font-family")?.replace(/['"]/g, "").toLowerCase();
            if (fontFamily && usedFonts.has(fontFamily)) {
              fontFaceProcessingPromises.push(embedSingleFontFace(rule));
            }
          } else if (rule.cssText) {
            regularCssTexts.push(rule.cssText);
          }
        });
      }
    });

    // Wait for all CSS-based font embedding to complete
    const embeddedFontFaces = (await Promise.allSettled(fontFaceProcessingPromises))
      .filter((result) => result.status === "fulfilled")
      .map((result) => (result as PromiseFulfilledResult<string>).value);

    // Also embed fonts from Google Fonts links (only used fonts)
    const googleFonts = await embedGoogleFonts(usedFonts);

    // Also embed fonts from document.fonts API (only used fonts)
    const documentFonts = await embedDocumentFonts(usedFonts);

    // Combine regular CSS with embedded font faces from all sources
    const combinedCss = [...regularCssTexts, ...embeddedFontFaces, ...googleFonts, ...documentFonts].join("\n");

    // Create the complete HTML document
    const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Oppizi Flyer Export - ${format} (${orientationInfo.orientation})</title>
    <style>
        ${combinedCss}
        .tl-overlays{
            display: ${displaySafetyAndCutLines ? "block" : "none"} !important;
        }
        /* Safety and cut line shapes visibility */
        .tl-shape[data-shape-type="foldLine"],
        .tl-shape[data-shape-id="shape:frontBorderDashLine"],
        .tl-shape[data-shape-id="shape:backBorderDashLine"],
        .tl-shape[data-shape-id="shape:frontRedLine"],
        .tl-shape[data-shape-id="shape:backRedLine"],
        .tl-shape[data-shape-id="shape:diecutLineFront"],
        .tl-shape[data-shape-id="shape:diecutLineBack"],
        .tl-shape[data-shape-id="shape:cutLineFront"],
        .tl-shape[data-shape-id="shape:cutLineBack"],
        .tl-shape[data-shape-id="shape:a6SafetyLineFront"],
        .tl-shape[data-shape-id="shape:a6SafetyLineBack"],
        .tl-shape[data-shape-id="shape:TearawayCutLineFront"],
        .tl-shape[data-shape-id="shape:TearawayCutLineBack"],
        .tl-shape[data-shape-id="shape:tearawayFlyerBuilderCutlineFront"],
        .tl-shape[data-shape-id="shape:tearawayFlyerBuilderCutlineBack"],
        .tl-shape[data-shape-id="shape:tearawaySafetyLineFront"],
        .tl-shape[data-shape-id="shape:tearawaySafetyLineBack"] {
            display: ${displaySafetyAndCutLines ? "block" : "none"} !important;
            visibility: ${displaySafetyAndCutLines ? "visible" : "hidden"} !important;
            opacity: ${displaySafetyAndCutLines ? "1" : "0"} !important;
        }
        body {
            margin: 0;
            padding: 0;
            background-color: white;
            overflow: hidden;
            ${
              useCustomTwoPageLayout
                ? `width: ${orientationInfo.displayWidth * 2}px; height: ${orientationInfo.displayHeight}px;`
                : `width: ${orientationInfo.displayWidth}px; height: ${orientationInfo.displayHeight}px;`
            }
        }
        /* Container positioning - allows natural clipping of overflowing elements */
        .tl-html-layer {
            position: relative !important;
            top: 0 !important;
            left: 0 !important;
            width: ${useCustomTwoPageLayout ? "auto" : `${orientationInfo.displayWidth}px`} !important;
            height: ${useCustomTwoPageLayout ? "auto" : `${orientationInfo.displayHeight}px`} !important;
            transform: none !important;
            contain: none !important;
            padding-left: 0 !important;
            margin-left: 0 !important;
            overflow: hidden !important;
        }
        
        .tl-html-layer > .tl-shape {
            transform-origin: top left !important;
        }
        
        ${
          useCustomTwoPageLayout
            ? `
        .pages-container {
            display: flex;
            flex-direction: row;
            width: 100%;
            height: 100%;
            margin: 0;
            padding: 0;
        }
        
        .page {
            width: ${orientationInfo.displayWidth}px;
            height: ${orientationInfo.displayHeight}px;
            position: relative;
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            overflow: hidden;
            flex-shrink: 0;
        }
        
        .page-content {
            position: relative;
            width: 100%;
            height: 100%;
        }
        
        .front-page, .back-page {
            display: block;
        }
        
        /* Ensure shapes maintain their positioning within each page */
        .page-content .tl-shape {
            position: absolute;
        }
        
        /* Reset transforms for back page shapes to position them correctly */
        .back-content .tl-shape {
            transform-origin: top left !important;
        }
        `
            : ""
        }
    </style>
</head>
<body data-format="${format}" data-orientation="${orientationInfo.orientation}" data-is-portrait="${
      orientationInfo.isPortrait
    }" data-use-custom-two-page-layout="${useCustomTwoPageLayout}" data-width-mm="${
      orientationInfo.widthInMm
    }" data-height-mm="${orientationInfo.heightInMm}" data-width-px="${orientationInfo.displayWidth}" data-height-px="${
      orientationInfo.displayHeight
    }" data-display-safety-and-cut-lines="${displaySafetyAndCutLines}">
    
    ${useCustomTwoPageLayout ? generateTwoPageLayout(elementClone, format) : elementClone.outerHTML}
</body>
</html>`;

    // 🆕 NEW: Restore original canvas positions after export (in reverse order)
    if (restoreCanvasPositions) {
      try {
        restoreCanvasPositions();
        // Give the UI a moment to update after restoring positions
        await new Promise((resolve) => setTimeout(resolve, 50));
      } catch (error) {
        console.error("❌ Failed to restore canvas positions:", error);
      }
    }

    // 🆕 NEW: Remove temporary forcedLayerCategory assignments
    if (restoreForcedCategories) {
      try {
        restoreForcedCategories();
        // Give the UI a moment to update after removing categories
        await new Promise((resolve) => setTimeout(resolve, 50));
      } catch (error) {
        console.error("❌ Failed to restore forced categories:", error);
      }
    }

    // 🎯 Restore original camera position after export
    // Skip restore if we skipped adjustment (auto-save) OR if keepCameraPosition is true
    if (tldrawEditor && originalCamera && !skipCameraAdjustment && !keepCameraPosition) {
      try {
        if (typeof tldrawEditor.setCamera === "function") {
          // New Tldraw Editor API
          tldrawEditor.setCamera(originalCamera, {
            animation: { duration: 0 }, // No animation for instant restore
          });
        }
      } catch (restoreError) {
        console.warn("⚠️ Could not restore camera position:", restoreError);
      }
    } else if (keepCameraPosition) {
      // Camera already centered with animation before capture - nothing to do
      // Visual centering happened at the beginning, HTML was captured after
    }

    // Return HTML string if requested
    if (returnHtml) {
      return htmlContent;
    }

    // Otherwise, trigger download to @tmp/ directory
    const tmpFileName = `@tmp/${fileName}`;
    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = tmpFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("❌ Export failed:", error);
    throw error;
  }
}

export default simpleHtmlExport;
